import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../lib/supabaseClient';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: false };
  }

  componentDidCatch(error, errorInfo) {
    if (error.message !== 'Timeout (b)') {
      console.error('Error:', error);
    }
  }

  render() {
    return this.props.children;
  }
}

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '90%',
        maxHeight: '80vh',
        overflowY: 'auto',
        position: 'relative',
        color: 'white',
        border: '1px solid #333'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            right: '10px',
            top: '10px',
            background: 'none',
            border: 'none',
            color: '#fff',
            fontSize: '20px',
            cursor: 'pointer'
          }}
        >
          ×
        </button>
        <div style={{ marginTop: '20px' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const RejectedSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedNotes, setSelectedNotes] = useState('');
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    return () => {
      // Cleanup function to prevent state updates after unmount
      setSubmissions([]);
      setLoading(true);
      setUser(null);
      setSelectedNotes('');
      setIsNotesModalOpen(false);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
      } else {
        setUser(session.user);
        fetchSubmissions();
      }
    } catch (error) {
      if (error.message !== 'Timeout (b)') {
        console.error('Auth error:', error);
      }
    }
  };

  const fetchSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('rejected_submissions')
        .select('*')
        .order('action_date', { ascending: false });

      if (error) throw error;
      
      // Only update state if component is still mounted
      setSubmissions(data || []);
    } catch (error) {
      if (error.message !== 'Timeout (b)') {
        console.error('Error fetching submissions:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewNotes = (notes) => {
    setSelectedNotes(notes);
    setIsNotesModalOpen(true);
  };

  const handleDelete = async (submission) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('rejected_submissions')
        .delete()
        .eq('id', submission.id);

      if (error) throw error;

      setSubmissions(submissions.filter(s => s.id !== submission.id));
      alert('Submission deleted successfully');
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Failed to delete submission');
    }
  };

  const handleRestore = async (submission) => {
    try {
      console.log('Attempting to restore submission:', submission);

      // First delete from rejected_submissions
      const { error: deleteError } = await supabase
        .from('rejected_submissions')
        .delete()
        .eq('id', submission.id);

      if (deleteError) {
        console.error('Delete Error Details:', {
          message: deleteError.message,
          details: deleteError.details,
          hint: deleteError.hint
        });
        throw deleteError;
      }

      // Then use submit_demo RPC with RESTORE_ACTION token
      const { error: submitError } = await supabase.rpc('submit_demo', {
        name_in: submission.name,
        email_in: submission.email,
        song_name_in: submission.song_name,
        song_link_in: submission.song_link,
        instagram_in: submission.instagram || '',
        notes_in: submission.notes || '',
        captcha_token_in: 'RESTORE_ACTION'
      });

      if (submitError) {
        console.error('Submit Error Details:', {
          message: submitError.message,
          details: submitError.details,
          hint: submitError.hint
        });
        // If submit fails, try to restore the deleted submission
        const { error: restoreError } = await supabase
          .from('rejected_submissions')
          .insert(submission);
        
        if (restoreError) {
          console.error('Failed to restore deleted submission:', restoreError);
        }
        throw submitError;
      }

      setSubmissions(submissions.filter(s => s.id !== submission.id));
      alert('Submission restored successfully');
    } catch (error) {
      console.error('Full error object:', error);
      alert(`Failed to restore submission: ${error.message}`);
    }
  };

  const tableHeaderStyle = { padding: '12px 15px', textAlign: 'left', borderBottom: '1px solid #333', color: '#f44336', fontWeight: 'bold' };
  const tableRowStyle = { borderBottom: '1px solid #222' };
  const tableCellStyle = { padding: '12px 15px', color: '#fff' };
  const buttonStyle = { padding: '10px 20px', border: '1px solid white', borderRadius: '4px', color: 'white', cursor: 'pointer', background: 'transparent' };
  const linkStyle = { color: '#4CAF50', textDecoration: 'none', wordBreak: 'break-all' };
  const viewNotesStyle = { 
    color: '#f44336', 
    textDecoration: 'underline', 
    cursor: 'pointer', 
    background: 'none',
    border: 'none',
    padding: 0
  };
  const actionButtonStyle = {
    padding: '6px 12px',
    border: 'none',
    borderRadius: '4px',
    color: 'white',
    cursor: 'pointer',
    marginRight: '8px'
  };

  if (loading) {
    return <p style={{ color: 'white', padding: '20px' }}>Loading...</p>;
  }

  return (
    <ErrorBoundary>
      <div style={{ backgroundColor: '#000000', minHeight: '100vh', padding: '40px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', color: 'white' }}>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', justifyContent: 'center' }}>
            <button onClick={() => navigate('/admin')} style={buttonStyle}>Back to Admin</button>
          </div>
          <h1 style={{ marginBottom: '10px', textAlign: 'center' }}>Rejected Submissions</h1>
          {user && (<h2 style={{ marginBottom: '30px', color: '#f44336', fontSize: '18px', fontWeight: 'normal', textAlign: 'center' }}>Hello, {user.email}</h2>)}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'rgba(0, 0, 0, 0.3)', backdropFilter: 'blur(10px)' }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Action Date</th>
                  <th style={tableHeaderStyle}>Name</th>
                  <th style={tableHeaderStyle}>Email</th>
                  <th style={tableHeaderStyle}>Instagram</th>
                  <th style={tableHeaderStyle}>Song Name</th>
                  <th style={tableHeaderStyle}>Song Link</th>
                  <th style={tableHeaderStyle}>Notes</th>
                  <th style={tableHeaderStyle}>Actioned By</th>
                  <th style={tableHeaderStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission) => (
                  <tr key={submission.id} style={tableRowStyle}>
                    <td style={tableCellStyle}>{new Date(submission.action_date).toLocaleDateString()}</td>
                    <td style={tableCellStyle}>{submission.name}</td>
                    <td style={tableCellStyle}>{submission.email}</td>
                    <td style={tableCellStyle}>{submission.instagram}</td>
                    <td style={tableCellStyle}>{submission.song_name}</td>
                    <td style={tableCellStyle}>
                      <a 
                        href={submission.song_link.startsWith('http') ? submission.song_link : `https://${submission.song_link}`}
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={linkStyle}
                      >
                        {submission.song_link}
                      </a>
                    </td>
                    <td style={tableCellStyle}>
                      {submission.notes && (
                        <button 
                          onClick={() => handleViewNotes(submission.notes)}
                          style={viewNotesStyle}
                        >
                          View Notes
                        </button>
                      )}
                    </td>
                    <td style={tableCellStyle}>{submission.actioned_by}</td>
                    <td style={tableCellStyle}>
                      <button
                        onClick={() => handleRestore(submission)}
                        style={{
                          ...actionButtonStyle,
                          background: '#4CAF50'
                        }}
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => handleDelete(submission)}
                        style={{
                          ...actionButtonStyle,
                          background: '#f44336'
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes Modal */}
        <Modal 
          isOpen={isNotesModalOpen} 
          onClose={() => setIsNotesModalOpen(false)}
        >
          <h3 style={{ marginBottom: '15px', color: '#f44336' }}>Notes</h3>
          <p style={{ 
            whiteSpace: 'pre-wrap', 
            wordBreak: 'break-word',
            lineHeight: '1.5',
            fontSize: '14px'
          }}>
            {selectedNotes}
          </p>
        </Modal>
      </div>
    </ErrorBoundary>
  );
};

export default RejectedSubmissions;